/**
 * WealthGraph - Interactive wealth projection visualization
 */

import * as React from 'react'
import {
  Area,
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { WealthProjection, LifeEvent } from '@/types'
import { CATEGORY_COLORS } from '@/types/timeline.types'
import { formatCurrency } from '@/lib/utils'
import { TrendingUp } from 'lucide-react'

interface WealthGraphProps {
  projection: WealthProjection
  events: LifeEvent[]
  currentYear: number
  retirementYear: number
  onYearClick?: (year: number) => void
  onEventClick?: (eventId: string) => void
  className?: string
}

type ViewMode = 'netWorth' | 'assets' | 'breakdown'

export function WealthGraph({
  projection,
  events,
  currentYear,
  retirementYear,
  onYearClick,
  onEventClick,
  className,
}: WealthGraphProps) {
  const [viewMode, setViewMode] = React.useState<ViewMode>('netWorth')
  const [showConfidenceBands, setShowConfidenceBands] = React.useState(true)

  // Format data for recharts
  const chartData = React.useMemo(() => {
    return projection.points.map((point) => ({
      year: point.year,
      age: point.age,
      netWorth: point.netWorth,
      optimistic: point.optimistic,
      pessimistic: point.pessimistic,
      liquidAssets: point.liquidAssets,
      retirementAssets: point.retirementAssets,
      realEstateEquity: point.realEstateEquity,
      totalDebts: point.totalDebts,
      totalAssets: point.totalAssets,
      hasEvents: point.eventsThisYear.length > 0,
      eventsThisYear: point.eventsThisYear,
    }))
  }, [projection.points])

  // Get key milestones
  const milestones = React.useMemo(() => {
    const result: { year: number; label: string; value: number; color: string }[] = []
    
    // Current year
    const currentPoint = chartData.find(p => p.year === currentYear)
    if (currentPoint) {
      result.push({
        year: currentYear,
        label: 'Today',
        value: currentPoint.netWorth,
        color: '#3b82f6',
      })
    }
    
    // Retirement
    const retirementPoint = chartData.find(p => p.year === retirementYear)
    if (retirementPoint) {
      result.push({
        year: retirementYear,
        label: 'Retirement',
        value: retirementPoint.netWorth,
        color: '#f59e0b',
      })
    }
    
    // Peak net worth
    const peakPoint = chartData.reduce((max, p) => 
      p.netWorth > max.netWorth ? p : max, chartData[0])
    if (peakPoint && peakPoint.year !== retirementYear) {
      result.push({
        year: peakPoint.year,
        label: 'Peak',
        value: peakPoint.netWorth,
        color: '#22c55e',
      })
    }
    
    return result
  }, [chartData, currentYear, retirementYear])

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: {
    active?: boolean
    payload?: Array<{ value: number; dataKey: string; payload: typeof chartData[0] }>
    label?: number
  }) => {
    if (!active || !payload || payload.length === 0) return null
    
    const data = payload[0].payload
    const yearEvents = events.filter(e => data.eventsThisYear.includes(e.id))
    
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3 max-w-[280px]">
        <div className="font-semibold mb-2">
          {label} (Age {data.age})
        </div>
        
        <div className="space-y-1 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Net Worth:</span>
            <span className="font-medium">{formatCurrency(data.netWorth)}</span>
          </div>
          
          {viewMode === 'breakdown' && (
            <>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Liquid:</span>
                <span>{formatCurrency(data.liquidAssets)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Retirement:</span>
                <span>{formatCurrency(data.retirementAssets)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Real Estate:</span>
                <span>{formatCurrency(data.realEstateEquity)}</span>
              </div>
              <div className="flex justify-between gap-4 text-red-500">
                <span>Debt:</span>
                <span>-{formatCurrency(data.totalDebts)}</span>
              </div>
            </>
          )}
          
          {showConfidenceBands && (
            <div className="pt-2 mt-2 border-t text-xs">
              <div className="flex justify-between gap-4">
                <span className="text-green-500">Optimistic:</span>
                <span>{formatCurrency(data.optimistic)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-red-500">Pessimistic:</span>
                <span>{formatCurrency(data.pessimistic)}</span>
              </div>
            </div>
          )}
          
          {yearEvents.length > 0 && (
            <div className="pt-2 mt-2 border-t">
              <div className="text-xs font-medium mb-1">Events:</div>
              {yearEvents.map(event => (
                <div key={event.id} className="text-xs text-muted-foreground">
                  • {event.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  // Event markers on chart
  const eventMarkers = React.useMemo(() => {
    const markers: { year: number; event: LifeEvent }[] = []
    events.forEach(event => {
      const eventYear = new Date(event.date).getFullYear()
      const point = chartData.find(p => p.year === eventYear)
      if (point) {
        markers.push({ year: eventYear, event })
      }
    })
    return markers
  }, [events, chartData])

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Wealth Projection
            </CardTitle>
            <CardDescription>
              Your projected net worth in today's dollars
            </CardDescription>
          </div>
          
          <div className="flex items-center gap-2">
            <Select
              value={viewMode}
              onValueChange={(v) => setViewMode(v as ViewMode)}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="netWorth">Net Worth</SelectItem>
                <SelectItem value="assets">Total Assets</SelectItem>
                <SelectItem value="breakdown">Breakdown</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant={showConfidenceBands ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setShowConfidenceBands(!showConfidenceBands)}
            >
              Bands
            </Button>
          </div>
        </div>
        
        {/* Milestone badges */}
        <div className="flex flex-wrap gap-2 pt-2">
          {milestones.map((milestone) => (
            <Badge
              key={`${milestone.label}-${milestone.year}`}
              variant="outline"
              className="cursor-pointer hover:bg-muted"
              onClick={() => onYearClick?.(milestone.year)}
            >
              <div
                className="w-2 h-2 rounded-full mr-2"
                style={{ backgroundColor: milestone.color }}
              />
              {milestone.label}: {formatCurrency(milestone.value)}
            </Badge>
          ))}
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
              onClick={(e) => {
                if (e?.activePayload?.[0]) {
                  const year = e.activePayload[0].payload.year
                  onYearClick?.(year)
                }
              }}
            >
              <defs>
                <linearGradient id="netWorthGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="confidenceGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.1} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
              </defs>
              
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              
              <XAxis
                dataKey="year"
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
                tickFormatter={(value) => `'${String(value).slice(2)}`}
              />
              
              <YAxis
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => formatCurrency(value, 0)}
              />
              
              <Tooltip content={<CustomTooltip />} />
              
              {/* Confidence bands */}
              {showConfidenceBands && (
                <>
                  <Area
                    type="monotone"
                    dataKey="optimistic"
                    stroke="none"
                    fill="#22c55e"
                    fillOpacity={0.1}
                    name="Optimistic"
                  />
                  <Area
                    type="monotone"
                    dataKey="pessimistic"
                    stroke="none"
                    fill="#ef4444"
                    fillOpacity={0.1}
                    name="Pessimistic"
                  />
                </>
              )}
              
              {/* Main net worth area */}
              {viewMode === 'breakdown' ? (
                <>
                  <Area
                    type="monotone"
                    dataKey="liquidAssets"
                    stackId="1"
                    stroke="#3b82f6"
                    fill="#3b82f6"
                    fillOpacity={0.6}
                    name="Liquid"
                  />
                  <Area
                    type="monotone"
                    dataKey="retirementAssets"
                    stackId="1"
                    stroke="#8b5cf6"
                    fill="#8b5cf6"
                    fillOpacity={0.6}
                    name="Retirement"
                  />
                  <Area
                    type="monotone"
                    dataKey="realEstateEquity"
                    stackId="1"
                    stroke="#10b981"
                    fill="#10b981"
                    fillOpacity={0.6}
                    name="Real Estate"
                  />
                </>
              ) : viewMode === 'assets' ? (
                <Area
                  type="monotone"
                  dataKey="totalAssets"
                  stroke="#22c55e"
                  fill="#22c55e"
                  fillOpacity={0.3}
                  strokeWidth={2}
                  name="Total Assets"
                />
              ) : (
                <Area
                  type="monotone"
                  dataKey="netWorth"
                  stroke="#3b82f6"
                  fill="url(#netWorthGradient)"
                  strokeWidth={2}
                  name="Net Worth"
                />
              )}
              
              {/* Current year line */}
              <ReferenceLine
                x={currentYear}
                stroke="#3b82f6"
                strokeDasharray="5 5"
                label={{
                  value: 'Today',
                  position: 'top',
                  fill: '#3b82f6',
                  fontSize: 12,
                }}
              />
              
              {/* Retirement line */}
              <ReferenceLine
                x={retirementYear}
                stroke="#f59e0b"
                strokeDasharray="5 5"
                label={{
                  value: 'Retire',
                  position: 'top',
                  fill: '#f59e0b',
                  fontSize: 12,
                }}
              />
              
              {/* Event markers */}
              {eventMarkers.slice(0, 10).map(({ year, event }) => {
                const point = chartData.find(p => p.year === year)
                if (!point) return null
                
                return (
                  <ReferenceDot
                    key={event.id}
                    x={year}
                    y={point.netWorth}
                    r={6}
                    fill={CATEGORY_COLORS[event.category]}
                    stroke="#fff"
                    strokeWidth={2}
                    onClick={() => onEventClick?.(event.id)}
                    style={{ cursor: 'pointer' }}
                  />
                )
              })}
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
        {/* Legend */}
        <div className="flex flex-wrap items-center justify-center gap-4 mt-4 text-sm">
          {viewMode === 'breakdown' ? (
            <>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-blue-500" />
                <span>Liquid Assets</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-violet-500" />
                <span>Retirement</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-emerald-500" />
                <span>Real Estate</span>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-blue-500" />
                <span>{viewMode === 'assets' ? 'Total Assets' : 'Net Worth'}</span>
              </div>
              {showConfidenceBands && (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-green-500/30" />
                    <span>Optimistic</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-red-500/30" />
                    <span>Pessimistic</span>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        
        {/* Assumptions note */}
        <p className="text-xs text-muted-foreground text-center mt-4">
          All values in today's dollars. Assumes {projection.assumptions.inflationRate}% inflation, {' '}
          {projection.assumptions.stockReturn}% stock returns, {projection.assumptions.bondReturn}% bond returns.
        </p>
      </CardContent>
    </Card>
  )
}
